import Button from '@mui/material/Button';
import Card from "@mui/material/Card";
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { NotificationsOutlined } from '@mui/icons-material';
import ArgonBox from "components/ArgonBox";
import FacturaSkeleton from "components/SkeletonLoader/FacturaSkeleton";
import Cookies from "js-cookie";
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";

function SubirComprobante() {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const { loading: userLoading, isDataLoaded } = useUser();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };


    useEffect(() => {
      // Kickout solo si Firebase confirma que no hay usuario autenticado
      if (!authLoading && !user) {
        navigate("/authentication/sign-in");
        return;
      }
    }, [navigate, user, authLoading]);


  // eslint-disable-next-line no-unused-vars -- reservado para uso futuro
  const _obtenerYGuardarUserId = async () => {
    try {
      const response = await fetch('https://squid-app-53gqb.ondigitalocean.app/datos-usuario', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await getToken()}`
        },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Error al obtener el userId');
      
      const data = await response.json();
      Cookies.set('userId', data.id);
      console.log("User ID obtenido y guardado en cookies:", data.id);
    } catch (error) {
      console.error('Error al obtener y guardar userId:', error);
    }
  };


  const registrarAccion = async (accion, detalles = '') => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        console.error('No se pudo obtener el userId de las cookies');
        return;
      }

      const response = await fetch('https://squid-app-53gqb.ondigitalocean.app/registrar-accion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          userId,
          accion,
          detalles
        })
      });
      

      if (!response.ok) throw new Error('Error al registrar la acción');
      console.log('Acción registrada correctamente');
    } catch (error) {
      console.error('Error al registrar la acción:', error);
    }
  };

  const onDrop = (acceptedFiles) => {
    setFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: '.csv, .xlsx',
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("UPLOADCARE_PUB_KEY", "405eb44180f11fa7d9e3");
    formData.append("UPLOADCARE_STORE", "auto");
    formData.append("file", file);

    try {
      const response = await fetch("https://upload.uploadcare.com/base/", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Archivo ${file.name} subido exitosamente con ID: ${result.file}`);
        const accion = "Subir Factura";
        const detalles = "Factura subida exitosamente";
        await registrarAccion(accion, detalles);
        setOpenDialog(false);
      } else {
        const error = await response.json();
        console.error("Error:", error);
        alert(`Error al subir archivo: ${error.detail}`);
      }
    } catch (error) {
      console.error("Network error:", error);
      alert("Error de red al intentar subir el archivo");
    } finally {
      setLoading(false);
    }
  };


  if (userLoading && !isDataLoaded) {
    return <FacturaSkeleton />;
  }


  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        paddingBottom: "100px", // Espacio para el tabbar
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* Header con título centrado y notificación */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "400px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "40px",
          marginTop: "20px",
          position: "relative",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: "#000",
            fontSize: { xs: "20px", md: "24px" },
            textAlign: "center",
          }}
        >
          Adjuntar Factura
        </Typography>
        <IconButton 
          onClick={handleNotificationsClick}
          sx={{ 
            color: "#666",
            position: "absolute",
            right: 0,
          }}
        >
          <NotificationsOutlined />
        </IconButton>
      </Box>

      {/* Contenedor centrado para la card */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          width: "100%",
          paddingTop: "1vh",
          minHeight: "60vh"
        }}
      >
        {/* Card principal */}
        <Card sx={{ 
          width: '100%', 
          maxWidth: 400, 
          minHeight: '400px',
          borderRadius: '16px', 
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#ffffff',
          position: 'relative'
        }}>
          <ArgonBox 
            p={4} 
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center' 
            }}
          >
            {/* Área de dropzone */}
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #1F85F6',
                borderRadius: '12px',
                padding: '150px 20px',
                minHeight: '200px',
                textAlign: 'center',
                backgroundColor: '#F8F9FA',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: '#EBF4FF',
                  borderColor: '#1976D2'
                }
              }}
            >
              <input {...getInputProps()} />
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666666',
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              >
                Tocá para seleccionar un archivo
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#999999',
                  fontSize: '12px'
                }}
              >
                o arrastrá y soltá 
              </Typography>
            </Box>

            {file && (
              <Box sx={{ marginTop: '24px' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000',
                    fontSize: '14px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}
                >
                  Archivo seleccionado: {file.name}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setOpenDialog(true)}
                  sx={{ 
                    backgroundColor: '#1F85F6',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '12px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#1976D2'
                    }
                  }}
                >
                  Subir archivo
                </Button>
              </Box>
            )}
          </ArgonBox>
        </Card>
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirmación de carga</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres subir este archivo?
          </DialogContentText>
          {loading && <CircularProgress />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">Cancelar</Button>
          <Button onClick={handleUpload} color="primary" disabled={loading}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* Popover de notificaciones */}
      <Popover
        open={Boolean(notificationsAnchorEl)}
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            minWidth: '200px',
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
            No hay notificaciones pendientes
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
}

export default SubirComprobante;
